// R-SIS-1 — Jalali (Shamsi) date picker.
//
// The §21.4 "Calendar (شمسی)" component in its date-selection mode. Admins
// enter term/window dates in Jalali; the value in/out is a Gregorian ISO
// string (what the API stores). A month-grid popover handles selection;
// the input shows the formatted Shamsi date read-only (typing a free-form
// Jalali date is error-prone, so selection is via the grid).
//
// Month/week *view* mode (for displaying events in the weekly schedule /
// admin calendar) is a later additive concern — R-SIS-3+ extends this.
//
// A11y: toggle button + grid day buttons are real <button>s with labels;
// Escape + click-outside close; selected day is aria-pressed.

import React from "react";

import { Icon } from "../icons";
import {
  JALALI_MONTHS_FA,
  JALALI_WEEKDAYS_FA,
  dateToJalali,
  formatJalaliShort,
  jalaliMonthLength,
  jalaliToDate,
  jalaliToIso,
  toFaDigits,
} from "../i18n/jalali";

interface JalaliDatePickerProps {
  value: string | null;
  onChange: (iso: string | null) => void;
  id?: string;
  placeholder?: string;
  /** Show a clear (×) action when a value is set. Default true. */
  clearable?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
}

// getDay(): 0=Sun..6=Sat. Persian week starts Saturday → shift so Sat=0.
const persianWeekday = (d: Date): number => (d.getDay() + 1) % 7;

export const JalaliDatePicker: React.FC<JalaliDatePickerProps> = ({
  value,
  onChange,
  id,
  placeholder = "انتخاب تاریخ",
  clearable = true,
  disabled = false,
  ariaLabel,
}) => {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);

  // The selected day (Jalali), derived from the ISO value.
  const selected = React.useMemo(() => {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : dateToJalali(d);
  }, [value]);

  // The month currently shown in the grid. Defaults to the selected
  // month, else today's month.
  const [view, setView] = React.useState(() => {
    const base = selected ?? dateToJalali(new Date());
    return { jy: base.jy, jm: base.jm };
  });

  // Re-sync the view to the selected month whenever the popover opens.
  React.useEffect(() => {
    if (open) {
      const base = selected ?? dateToJalali(new Date());
      setView({ jy: base.jy, jm: base.jm });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on click-outside + Escape.
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const stepMonth = (delta: number): void => {
    setView((v) => {
      let jm = v.jm + delta;
      let jy = v.jy;
      if (jm < 1) {
        jm = 12;
        jy -= 1;
      } else if (jm > 12) {
        jm = 1;
        jy += 1;
      }
      return { jy, jm };
    });
  };

  const pick = (jd: number): void => {
    onChange(jaliToIsoSafe(view.jy, view.jm, jd));
    setOpen(false);
  };

  const monthLen = jalaliMonthLength(view.jy, view.jm);
  const firstWeekday = persianWeekday(jalaliToDate(view.jy, view.jm, 1));
  const today = dateToJalali(new Date());
  const cells: (number | null)[] = [
    ...Array(firstWeekday).fill(null),
    ...Array.from({ length: monthLen }, (_, i) => i + 1),
  ];

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        className="crud-form-input"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          width: "100%",
          cursor: disabled ? "not-allowed" : "pointer",
          textAlign: "right",
          fontFamily: "inherit",
          color: value ? "var(--fg)" : "var(--fg-dim)",
        }}
      >
        <span>{value ? formatJalaliShort(value) : placeholder}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {clearable && value ? (
            <span
              role="button"
              tabIndex={0}
              aria-label="پاک کردن تاریخ"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange(null);
                }
              }}
              style={{ display: "inline-flex", color: "var(--fg-mute)" }}
            >
              <Icon name="x" size={14} />
            </span>
          ) : null}
          <Icon name="calendar" size={16} />
        </span>
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="تقویم شمسی"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            insetInlineStart: 0,
            zIndex: 50,
            width: 280,
            padding: 12,
            background: "var(--surface)",
            border: "1px solid var(--line)",
            borderRadius: 12,
            boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <button type="button" className="btn-icon" aria-label="ماه بعد" onClick={() => stepMonth(1)}>
              <Icon name="chevronRight" size={18} />
            </button>
            <div style={{ fontWeight: 600, fontSize: 14 }}>
              {JALALI_MONTHS_FA[view.jm - 1]} {toFaDigits(view.jy)}
            </div>
            <button type="button" className="btn-icon" aria-label="ماه قبل" onClick={() => stepMonth(-1)}>
              <Icon name="chevronLeft" size={18} />
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
            {JALALI_WEEKDAYS_FA.map((w, i) => (
              <div key={i} style={{ textAlign: "center", fontSize: 11, color: "var(--fg-mute)", padding: "4px 0" }}>
                {w}
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
            {cells.map((jd, i) => {
              if (jd === null) return <div key={`e${i}`} />;
              const isSel = selected && selected.jy === view.jy && selected.jm === view.jm && selected.jd === jd;
              const isToday = today.jy === view.jy && today.jm === view.jm && today.jd === jd;
              return (
                <button
                  key={jd}
                  type="button"
                  aria-pressed={isSel || undefined}
                  onClick={() => pick(jd)}
                  style={{
                    aspectRatio: "1 / 1",
                    border: isToday && !isSel ? "1px solid var(--accent)" : "1px solid transparent",
                    borderRadius: 8,
                    background: isSel ? "var(--accent)" : "transparent",
                    color: isSel ? "var(--accent-on)" : "var(--fg)",
                    fontFamily: "inherit",
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  {toFaDigits(jd)}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

// Tiny wrapper so a bad pick can never throw into render.
const jaliToIsoSafe = (jy: number, jm: number, jd: number): string => jalaliToIso(jy, jm, jd);

export default JalaliDatePicker;
